
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { staffService } from '../services/staffService';
import { StoreStaff, StaffRole, Store, StaffActivity } from '../types';
import { 
  Users, 
  Plus, 
  Trash2, 
  Mail, 
  ShieldCheck, 
  UserCheck, 
  Clock, 
  X, 
  Shield, 
  Zap, 
  Loader2,
  Check,
  ChevronDown,
  ArrowRight,
  Activity,
  Lock,
  Eye,
  Settings2,
  Info
} from 'lucide-react';

interface AdminTeamProps {
  storeId: string;
}

const AdminTeam: React.FC<AdminTeamProps> = ({ storeId }) => {
  const [staff, setStaff] = useState<StoreStaff[]>([]);
  const [activities, setActivities] = useState<StaffActivity[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<StaffRole>(StaffRole.SUPPORT);
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setStaff(staffService.getStoreStaff(storeId));
    setActivities(storage.getStaffActivity(storeId).sort((a,b) => b.timestamp.localeCompare(a.timestamp)));
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    
    setIsInviting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    staffService.addStaffMember(storeId, inviteEmail, inviteRole);
    storage.logStaffActivity(storeId, {
      staffId: 'admin', // Placeholder for current user
      staffEmail: inviteEmail,
      action: `Invited as ${inviteRole}`
    });
    
    loadData();
    setIsInviting(false);
    setShowInviteModal(false);
    setInviteEmail('');
  };

  const handleRemove = (id: string, email: string) => {
    if (window.confirm(`Bhai, kya aap sach mein ${email} ko remove karna chahte hain?`)) {
      staffService.removeStaffMember(storeId, id);
      storage.logStaffActivity(storeId, {
        staffId: 'admin',
        staffEmail: email,
        action: `Removed from team`
      });
      loadData();
    }
  };

  const roleDetails = {
    [StaffRole.MANAGER]: {
      desc: 'Bhai, ye aapka right hand hai. Full control, minus final billing/plan changes.',
      perms: ['Analytics', 'Inventory', 'Orders', 'Marketing', 'Discounts', 'Team Management']
    },
    [StaffRole.FULFILLMENT]: {
      desc: 'Inka kaam hai product update karna aur logistics handle karna.',
      perms: ['Inventory Sync', 'Order Status', 'Shipping Labels', 'Stock Management']
    },
    [StaffRole.SUPPORT]: {
      desc: 'Customer communication aur product reviews ka master.',
      perms: ['Live Chat', 'Review Moderation', 'Order Status (View Only)']
    }
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Team Command Center</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Hierarchy & Governance Engine</p>
        </div>
        <button 
          onClick={() => setShowInviteModal(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Add Team Member
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-10">
            {/* Staff List */}
            <section className="space-y-6">
               <div className="flex items-center gap-2 border-b border-gray-100 pb-4">
                  <Users className="h-5 w-5 text-blue-600" />
                  <h3 className="text-xl font-black text-gray-900">Current Staff</h3>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {staff.length === 0 ? (
                    <div className="col-span-full bg-white p-20 rounded-[40px] border border-dashed border-gray-200 text-center space-y-4 opacity-50">
                       <Users className="h-16 w-16 mx-auto text-gray-300" />
                       <p className="font-black uppercase tracking-widest text-xs">Aap akele kaam kar rahe hain. Team invite karein!</p>
                    </div>
                  ) : (
                    staff.map((member) => (
                      <div key={member.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col justify-between group hover:border-blue-200 transition-all">
                         <div className="flex items-center gap-6 mb-8">
                            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center font-black text-xl shadow-inner border border-blue-100">
                               {member.email[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                               <p className="text-lg font-black text-gray-900 truncate">{member.email}</p>
                               <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${
                                     member.role === StaffRole.MANAGER ? 'bg-purple-50 text-purple-600 border-purple-100' :
                                     member.role === StaffRole.FULFILLMENT ? 'bg-orange-50 text-orange-600 border-orange-100' :
                                     'bg-blue-50 text-blue-600 border-blue-100'
                                  }`}>
                                     {member.role}
                                  </span>
                                  <div className="w-1 h-1 bg-gray-200 rounded-full"></div>
                                  <p className="text-[10px] text-gray-400 font-bold uppercase truncate">Joined {new Date(member.addedAt).toLocaleDateString()}</p>
                               </div>
                            </div>
                         </div>
                         <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                            <button className="flex items-center gap-2 text-[10px] font-black text-gray-400 hover:text-blue-600 transition-all uppercase tracking-widest">
                               <Settings2 className="h-3.5 w-3.5" /> Adjust Role
                            </button>
                            <button 
                              onClick={() => handleRemove(member.id, member.email)}
                              className="p-3 bg-red-50 text-red-400 hover:text-red-600 rounded-xl transition-all"
                            >
                               <Trash2 className="h-4 w-4" />
                            </button>
                         </div>
                      </div>
                    ))
                  )}
               </div>
            </section>

            {/* Permissions Matrix */}
            <section className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-2xl space-y-8">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-gray-900 text-white rounded-2xl shadow-xl shadow-gray-200">
                     <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                     <h3 className="text-xl font-black text-gray-900 tracking-tight">Permissions Matrix</h3>
                     <p className="text-[10px] text-blue-600 font-black uppercase tracking-widest">Governance Intelligence</p>
                  </div>
               </div>

               <div className="overflow-x-auto">
                  <table className="w-full text-left">
                     <thead>
                        <tr className="text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                           <th className="pb-4 pr-4">Permission Module</th>
                           <th className="pb-4 text-center">Owner</th>
                           <th className="pb-4 text-center">Manager</th>
                           <th className="pb-4 text-center">Fulfillment</th>
                           <th className="pb-4 text-center">Support</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-50">
                        {[
                           { name: 'Analytics & Revenue', roles: ['OWNER', 'MANAGER'] },
                           { name: 'Inventory Control', roles: ['OWNER', 'MANAGER', 'FULFILLMENT'] },
                           { name: 'Order Processing', roles: ['OWNER', 'MANAGER', 'FULFILLMENT'] },
                           { name: 'Marketing & Coupons', roles: ['OWNER', 'MANAGER'] },
                           { name: 'Customer Messages', roles: ['OWNER', 'MANAGER', 'SUPPORT'] },
                           { name: 'Finances & Payouts', roles: ['OWNER'] },
                           { name: 'Subscription & Billing', roles: ['OWNER'] },
                           { name: 'Audit Logs', roles: ['OWNER', 'MANAGER'] }
                        ].map((perm, i) => (
                           <tr key={i} className="hover:bg-gray-50/50 transition-all">
                              <td className="py-4 text-xs font-bold text-gray-700">{perm.name}</td>
                              <td className="py-4 text-center"><Check className="h-4 w-4 text-emerald-500 mx-auto" /></td>
                              <td className="py-4 text-center">
                                 {perm.roles.includes('MANAGER') ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <Lock className="h-3.5 w-3.5 text-gray-200 mx-auto" />}
                              </td>
                              <td className="py-4 text-center">
                                 {perm.roles.includes('FULFILLMENT') ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <Lock className="h-3.5 w-3.5 text-gray-200 mx-auto" />}
                              </td>
                              <td className="py-4 text-center">
                                 {perm.roles.includes('SUPPORT') ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <Lock className="h-3.5 w-3.5 text-gray-200 mx-auto" />}
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </section>
         </div>

         <div className="space-y-8">
            {/* Role Intelligence */}
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group border border-gray-800">
               <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><Shield className="h-24 w-24" /></div>
               <div className="relative space-y-8">
                  <h3 className="text-xl font-black tracking-tight">Role Definitions</h3>
                  <div className="space-y-6">
                     {(Object.keys(StaffRole) as StaffRole[]).map(role => (
                        <div key={role} className="space-y-2">
                           <h4 className="text-[10px] font-black uppercase text-blue-400 tracking-widest">{role}</h4>
                           <p className="text-xs text-gray-400 leading-relaxed italic">"{roleDetails[role].desc}"</p>
                        </div>
                     ))}
                  </div>
               </div>
            </div>

            {/* Audit Log */}
            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-6 relative overflow-hidden">
               <div className="flex items-center justify-between border-b border-gray-50 pb-4">
                  <div className="flex items-center gap-3">
                     <Activity className="h-5 w-5 text-emerald-500" />
                     <h3 className="text-lg font-black text-gray-900 tracking-tight uppercase">Recent Actions</h3>
                  </div>
               </div>
               <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                  {activities.length === 0 ? (
                    <p className="text-[10px] text-gray-400 font-bold uppercase text-center py-10 opacity-30">No recorded activity yet.</p>
                  ) : (
                    activities.map((act) => (
                      <div key={act.id} className="relative pl-6 pb-6 border-l border-gray-100 last:pb-0">
                         <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm"></div>
                         <div className="space-y-1">
                            <p className="text-xs font-black text-gray-900">{act.action}</p>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">{act.staffEmail}</p>
                            <p className="text-[8px] text-emerald-600 font-bold italic">{new Date(act.timestamp).toLocaleTimeString()}</p>
                         </div>
                      </div>
                    ))
                  )}
               </div>
               <button className="w-full py-4 bg-gray-50 text-gray-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all flex items-center justify-center gap-2">
                  <Eye className="h-3 w-3" /> View Full Audit
               </button>
            </div>

            <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 flex items-start gap-4">
               <Zap className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
               <p className="text-[10px] text-blue-700 font-medium leading-relaxed uppercase tracking-wider">
                  Bhai Tip: Use 'Fulfillment' role for warehouse staff to keep revenue data private.
               </p>
            </div>
         </div>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Deploy Team Asset</h3>
                 <button onClick={() => setShowInviteModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleInvite} className="p-10 space-y-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Employee Email</label>
                    <div className="relative">
                       <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                       <input 
                         required
                         type="email"
                         value={inviteEmail} 
                         onChange={e => setInviteEmail(e.target.value)}
                         placeholder="colleague@store.com" 
                         className="w-full pl-12 pr-6 py-4 bg-gray-50 border-none rounded-2xl font-bold focus:ring-2 focus:ring-blue-500" 
                       />
                    </div>
                 </div>

                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Assign Role Profile</label>
                    <div className="grid grid-cols-1 gap-3">
                       {(Object.keys(StaffRole) as StaffRole[]).map((role) => (
                         <button 
                           key={role}
                           type="button"
                           onClick={() => setInviteRole(role)}
                           className={`p-5 rounded-3xl border-2 text-left transition-all ${inviteRole === role ? 'border-blue-600 bg-blue-50 shadow-lg' : 'border-gray-100 hover:border-gray-200'}`}
                         >
                            <div className="flex justify-between items-start mb-1">
                               <h4 className="font-black text-gray-900 uppercase tracking-widest text-sm">{role}</h4>
                               {inviteRole === role && <UserCheck className="h-4 w-4 text-blue-600" />}
                            </div>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">{roleDetails[role].perms.slice(0, 3).join(', ')}...</p>
                         </button>
                       ))}
                    </div>
                 </div>

                 <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 flex items-start gap-4">
                    <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-blue-700 font-medium leading-relaxed italic">
                       Email bhejte hi staff member ko invite link chala jayega. Woh apni dashboard access turant start kar sakte hain.
                    </p>
                 </div>

                 <button 
                   disabled={isInviting}
                   type="submit" 
                   className="w-full py-6 bg-blue-600 text-white rounded-[2.5rem] font-black text-lg shadow-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                 >
                    {isInviting ? <Loader2 className="h-6 w-6 animate-spin" /> : <>Activate Staff Access <ArrowRight className="h-5 w-5" /></>}
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;
